import React, { Component } from 'react'
import millify from 'millify'
import {
  departments,
  destinations,
  getDestinationForId,
  getDepartmentForId,
  opsAPI,
  sortByColourOrder,
  buildNameToColorMapping
} from '../api/retail'
import {
  Box,
  Button,
  DropButton,
  Heading,
  Main,
  Select,
  Stack,
  Text,
  ResponsiveContext,
  Grid
} from 'grommet'
import {
  calculateImpactMetrics,
  debounce,
  getTotalByKeyFromResults,
  groupByKeysForNivo,
  monthNames
} from '../libs/common'
import withOpsApi from './withOpsApi'
import { ResponsiveLine, ResponsiveLineCanvas } from '@nivo/line'
import { ResponsivePie, ResponsivePieCanvas } from '@nivo/pie'
import { ResponsiveBar, ResponsiveBarCanvas } from '@nivo/bar'

import { DropWithHeading, LoadingHeader, PageHeader } from './Common'
import { SelectMonthInYear } from './Select'
import { AppContext } from '../libs/context'
import { FormCalendar, FormDown, ShareRounded, Time } from 'grommet-icons'
import { DonationMetrics, PieToolTip } from './Donations'
import { graphColors, departmentColors, nivoTheme } from '../libs/theme'
import { AnimatePresence } from 'framer-motion'
import firebase from '../libs/firebase'
import moment from 'moment-timezone'
import { linearGradient } from '../libs/color'
import { setLongDateRange } from '../libs/date'
const NoDataCheck = ({
  noData = false,
  noDataMessage = '',
  background = 'white',
  children,
  ...rest
}) =>
  noData ? (
    <Box
      fill
      background={{ color: background }}
      pad="large"
      align={'center'}
      justify={'center'}
      {...rest}
    >
      <Text color={'dark-6'}>{noDataMessage}</Text>
    </Box>
  ) : (
    <Box {...rest}>{children}</Box>
  )
const BarToolTip = ({ id, formattedValue, color, indexValue }) => {
  return (
    <Box
      background={color}
      pad={'small'}
      round={'xsmall'}
      gap={'xsmall'}
      width={'small'}
      border={'xxsmall'}
      elevation={'large'}
    >
      <Text weight={'bold'} size={'small'} style={{ opacity: '80%' }}>
        {moment(indexValue).format('ddd, MMMM Do YYYY')}
      </Text>
      <Box fill={'horizontal'} gap={'small'} direction="row" justify="between">
        <Text weight="bold">{id}</Text>
        <Text weight="bold">{formattedValue}</Text>
      </Box>
    </Box>
  )
}
const LineToolTipItem = ({ serieColor = 'white', serieId, data }) => (
  <Box fill={'horizontal'} direction={'row'} justify="between" gap={'small'}>
    <Text
      size="small"
      color={serieColor === departmentColors.other ? departmentColors.otherDark : serieColor}
      weight={'bold'}
    >
      {serieId}
    </Text>
    <Text size="small" weight={'bold'} color={'white'}>
      {data.yFormatted}
    </Text>
  </Box>
)
const SliceToolTip = ({ slice }) => {
  const date = moment(slice.points[0] && slice.points[0].data.x).format('ddd, MMMM Do YYYY')
  let total = 0
  slice.points.forEach((point) => {
    total += point.data.y
  })
  return (
    <Box
      elevation={'large'}
      background={linearGradient('dark-1', 'dark-2')}
      round={'xsmall'}
      pad={'medium'}
      gap={'xsmall'}
      width={'small'}
    >
      <Text weight={'bold'} color={'white'}>
        {date}
      </Text>
      {slice.points.map((point) => (
        <LineToolTipItem key={point.id} {...point} />
      ))}
      <LineToolTipItem
        key={'total'}
        serieId={'Total'}
        data={{ yFormatted: millify(total) + ' kg' }}
      />
    </Box>
  )
}
export class _LineGraph extends Component {
  static contextType = ResponsiveContext
  static defaultProps = {
    fromApi: { results: [] },
    apiActions: {},
    show: false,
    legend: {}
  }
  state = { groupedForLine: {}, groupedForPie: {}, groupedForBar: { data: {}, values: [] } }
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.fromApi.results !== this.props.fromApi.results) {
      this.prepArray(this.props.fromApi.results)
    }
  }
  componentDidMount() {
    this.prepArray(this.props.fromApi.results)
  }

  prepArray = (results) => {
    const {
      graphOptions: { groupOptions }
    } = this.props
    const { groupedForLine, groupedForPie, groupedForBar } = groupByKeysForNivo(
      results,
      groupOptions
    )
    this.props.onResultsChanged && this.props.onResultsChanged(results)
    this.props.onGraphDataChanged &&
      this.props.onGraphDataChanged({ groupedForLine, groupedForPie, groupedForBar })

    this.setState({
      groupedForLine: groupedForLine,
      groupedForPie: groupedForPie,
      groupedForBar: groupedForBar
    })
  }
  formatAxisValueFromString = (d, precision) => {
    const dSplit = d.split('-')
    if (precision === 'month') {
      if (this.context === 'small') {
        if (parseInt(dSplit[1]) % 4 === 0) {
          return dSplit[0] + '-' + dSplit[1]
        }
        return ''
      } else {
        return dSplit[0] + '-' + dSplit[1]
      }
    } else if (precision === 'day') {
      if (this.context === 'small') {
        if (parseInt(dSplit[2]) % 4 === 0) {
          return dSplit[1] + '-' + dSplit[2]
        }
        return ''
      } else {
        return dSplit[1] + '-' + dSplit[2]
      }
    }
    return d
  }
  getPercentForPie = (value = 0, key) => {
    const { groupedForPie } = this.state
    let total = 0
    Object.values(groupedForPie[key]).forEach((d) => {
      total += d.value
    })
    return millify(total ? ((value || 0) / total) * 100 : 0, {
      precision: 0,
      units: ['%']
    })
  }

  render() {
    const {
      fromApi: { apiPath, apiLoading, resultCount },
      apiActions = {},
      apiFilterMenu,
      heading,
      dateInfo,
      show,
      step,
      legend,
      graphOptions: {
        groupOptions,
        responsive = {},
        bottomTickValues = 'every 2 days',
        xScalePrecision = 'day',
        rangeLabel = 'daily',
        xFormat = '%m-%d',
        colors = graphColors.meshBasic
      }
    } = this.props

    const { results, groupedForLine, groupedForPie, groupedForBar } = this.state
    const loading = apiLoading
    // const barDestinationColors = {
    //   Charity: meshTheme.global.colors['mesh-green-1'],
    //   Compost: meshTheme.global.colors['mesh-yellow-1'],
    //   Farm: meshTheme.global.colors['mesh-purple-1']
    // }

    const LineComponent = this.context === 'small' ? ResponsiveLineCanvas : ResponsiveLine
    const BarComponent = this.context === 'small' ? ResponsiveBarCanvas : ResponsiveBar
    const PieComponent = this.context === 'small' ? ResponsivePieCanvas : ResponsivePie

    return (
      show && (
        <ResponsiveContext.Consumer>
          {(size) => (
            <Box fill={'horizontal'} flex={false} gap={'medium'} pad={{ bottom: 'large' }}>
              <Heading color="dark-6" margin={{ vertical: 'xsmall' }}>
                {heading}
              </Heading>
              <Box pad="medium" flex={false} width={{ min: 'small', max: 'large' }}>
                <Grid
                  fill
                  justify={'start'}
                  columns={{
                    count: size === 'small' ? 3 : 8,
                    size: 'auto'
                  }}
                  gap={'small'}
                >
                  {Object.keys(legend).map((key) => (
                    <Box key={key} gap={'small'} direction={'row'} align={'center'}>
                      <Box
                        round={'full'}
                        background={legend[key]}
                        width={'tiny'}
                        height={'tiny'}
                        flex={false}
                      />
                      <Text truncate size="small" weight={'bold'}>
                        {key}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              </Box>
              {/*BarGraph*/}
              <Box flex="grow" gap="medium" direction={'row-responsive'}>
                <Box
                  round="small"
                  pad={'medium'}
                  background="white"
                  elevation="small"
                  flex={'grow'}
                  // flex={false}
                  gap={'small'}
                >
                  <Text weight={'bold'} color={'dark-6'}>
                    Bar graph of weight (kg) over time ({rangeLabel})
                  </Text>
                  <NoDataCheck
                    height={size === 'small' ? 'medium' : 'medium-medium'}
                    width={size === 'small' ? { min: 'small' } : { min: 'medium' }}
                    fill={'horizontal'}
                    noData={groupedForBar.values.length === 0}
                    noDataMessage={
                      'No data for this date range. Try changing the end month for reporting and/or report range.'
                    }
                  >
                    <BarComponent
                      theme={nivoTheme}
                      data={groupedForBar.values}
                      keys={groupedForBar.keys || []}
                      indexBy="id"
                      margin={{ top: 20, right: legend ? 10 : 130, bottom: 50, left: 40 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      // xScale={{ type: 'time' }}
                      xFormat="time:%Y-%m"
                      // valueFormat={{ format: ' >-.0%', enabled: true }}
                      colors={colors}
                      // colors={({ id }) => barDestinationColors[id]}
                      // defs={[
                      //   {
                      //     id: 'dots',
                      //     type: 'patternDots',
                      //     background: 'inherit',
                      //     color: '#38bcb2',
                      //     size: 4,
                      //     padding: 1,
                      //     stagger: true
                      //   },
                      //   {
                      //     id: 'lines',
                      //     type: 'patternLines',
                      //     background: 'inherit',
                      //     color: '#eed312',
                      //     rotation: -45,
                      //     lineWidth: 6,
                      //     spacing: 10
                      //   }
                      // ]}
                      // fill={[
                      //   {
                      //     match: {
                      //       id: 'fries'
                      //     },
                      //     id: 'dots'
                      //   },
                      //   {
                      //     match: {
                      //       id: 'sandwich'
                      //     },
                      //     id: 'lines'
                      //   }
                      // ]}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisTop={null}
                      axisRight={null}
                      valueFormat={(value) => millify(value) + ' kg'}
                      axisBottom={{
                        format: (d) => this.formatAxisValueFromString(d, xScalePrecision),

                        tickSize: 5,
                        tickPadding: 10,
                        tickRotation: -60,
                        // legend: 'date range',
                        legendPosition: 'middle'
                        // legendOffset: 60
                      }}
                      axisLeft={{
                        format: (value) => millify(value),
                        tickSize: 5,
                        tickPadding: 10,
                        tickRotation: 0,
                        // legend: 'weight (kg)',
                        legendPosition: 'middle'
                        // legendOffset: -60
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      labelTextColor={{ from: 'color', modifiers: [['brighter', 1.6]] }}
                      enableLabel={false}
                      tooltip={(props) => <BarToolTip {...props} />}
                      legends={
                        legend
                          ? []
                          : [
                            {
                              dataFrom: 'keys',
                              anchor: 'top-right',
                              direction: 'column',
                              justify: false,
                              translateX: 120,
                              translateY: 0,
                              itemsSpacing: 2,
                              itemWidth: 100,
                              itemHeight: 20,
                              itemDirection: 'left-to-right',
                              itemOpacity: 0.85,
                              symbolSize: 20,
                              effects: [
                                {
                                  on: 'hover',
                                  style: {
                                    itemOpacity: 1
                                  }
                                }
                              ]
                            }
                          ]
                      }
                    />
                  </NoDataCheck>
                </Box>
              </Box>
              {/*Line and Pie Graph*/}
              <Box flex="grow" gap="medium" direction={'row-responsive'}>
                <Box
                  round="small"
                  pad={'medium'}
                  background="white"
                  elevation="small"
                  // fill={'horizontal'}
                  // flex={'grow'}
                  width={size === 'small' ? { min: 'small' } : '60%'}
                  gap={'xsmall'}
                >
                  <Text weight={'bold'} color={'dark-6'}>
                    Line graph of weight (kg) over time ({rangeLabel})
                  </Text>
                  <Box
                    fill={'horizontal'}
                    height={'medium'}
                    width={size === 'small' ? { min: 'small' } : '60%'}
                  >
                    <LineComponent
                      theme={nivoTheme}
                      colors={colors}
                      data={
                        groupedForLine[groupOptions.keys[0]]
                          ? Object.values(groupedForLine[groupOptions.keys[0]])
                          : []
                      }
                      // data={test}
                      enableSlices={'x'}
                      margin={{ top: 20, right: legend ? 10 : 110, bottom: 50, left: 40 }}
                      xScale={{
                        type: 'time',
                        format: '%Y-%m-%d',
                        useUTC: false,
                        precision: xScalePrecision
                      }}
                      xFormat="time:%Y-%m-%d"
                      yFormat={(value) => millify(value) + ' kg'}
                      yScale={{
                        type: 'linear',
                        min: 0,
                        max: 'auto',
                        stacked: false,
                        reverse: false
                      }}
                      // yFormat=" >-.2f"
                      curve="monotoneX"
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        format: xFormat,
                        tickValues:
                          size === 'small' && responsive.bottomTickValues
                            ? responsive.bottomTickValues
                            : bottomTickValues,
                        // legend: 'date range',
                        orient: 'bottom',
                        tickSize: 5,
                        tickPadding: 10,
                        tickRotation: -60,
                        // legendOffset: 36,
                        legendPosition: 'middle'
                      }}
                      axisLeft={{
                        format: (value) => millify(value),
                        orient: 'left',
                        tickSize: 5,
                        tickPadding: 10,
                        tickRotation: 0,
                        // legend: 'weight (kg)',
                        // legendOffset: -40,
                        legendPosition: 'middle'
                      }}
                      enableGridX={false}
                      lineWidth={3}
                      // pointSize={10}
                      // pointColor={{ from: 'color', modifiers: [] }}
                      // pointBorderWidth={2}
                      // pointBorderColor={{ from: 'serieColor' }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      sliceTooltip={({ slice }) => <SliceToolTip slice={slice} />}
                      legends={
                        legend
                          ? []
                          : [
                            {
                              anchor: 'bottom-right',
                              direction: 'column',
                              justify: false,
                              translateX: 100,
                              translateY: 0,
                              itemsSpacing: 0,
                              itemDirection: 'left-to-right',
                              itemWidth: 80,
                              itemHeight: 20,
                              itemOpacity: 0.75,
                              symbolSize: 12,
                              symbolShape: 'circle',
                              symbolBorderColor: 'rgba(0, 0, 0, .5)',
                              effects: [
                                {
                                  on: 'hover',
                                  style: {
                                    itemBackground: 'rgba(0, 0, 0, .03)',
                                    itemOpacity: 1
                                  }
                                }
                              ]
                            }
                          ]
                      }
                    />
                  </Box>
                </Box>
                <Box
                  round="small"
                  pad={'medium'}
                  background="white"
                  elevation="small"
                  // flex={'grow'}
                  // flex={false}
                  gap={'small'}
                  width={size === 'small' ? { min: 'small' } : '40%'}

                // fill={'horizontal'}
                >
                  <Text weight={'bold'} color={'dark-6'}></Text>
                  <Box
                    fill={'horizontal'}
                    height={'medium'}
                    width={size === 'small' ? { min: 'small' } : '40%'}
                  >
                    <PieComponent
                      theme={nivoTheme}
                      data={
                        groupedForPie[groupOptions.keys[0]]
                          ? Object.values(groupedForPie[groupOptions.keys[0]])
                          : []
                      }
                      sortByValue={true}
                      margin={{ top: 20, right: 50, bottom: 20, left: 50 }}
                      innerRadius={0.5}
                      padAngle={0.9}
                      colors={colors}
                      cornerRadius={4}
                      valueFormat={(value) => millify(value) + ' kg'}
                      activeOuterRadiusOffset={8}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabel={(d) => this.getPercentForPie(d.value, groupOptions.keys[0])}
                      arcLinkLabelsTextColor={{ from: 'color' }}
                      arcLinkLabelsDiagonalLength={10}
                      arcLinkLabelsStraightLength={10}
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLinkLabelsSkipAngle={25}
                      arcLabelsSkipAngle={25}
                      tooltip={({ datum }) => <PieToolTip {...datum} />}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    // defs={[
                    //   {
                    //     id: 'dots',
                    //     type: 'patternDots',
                    //     background: 'inherit',
                    //     color: 'rgba(255, 255, 255, 0.3)',
                    //     size: 4,
                    //     padding: 1,
                    //     stagger: true
                    //   },
                    //   {
                    //     id: 'lines',
                    //     type: 'patternLines',
                    //     background: 'inherit',
                    //     color: 'rgba(255, 255, 255, 0.3)',
                    //     rotation: -45,
                    //     lineWidth: 6,
                    //     spacing: 10
                    //   }
                    // ]}
                    // fill={[
                    //   {
                    //     match: {
                    //       id: 'Farm'
                    //     },
                    //     id: 'dots'
                    //   },
                    //   {
                    //     match: {
                    //       id: 'Charity'
                    //     },
                    //     id: 'lines'
                    //   },
                    //   {
                    //     match: {
                    //       id: 'compost'
                    //     },
                    //     id: 'dots'
                    //   }
                    // ]}
                    // legends={[
                    //   {
                    //     anchor: 'bottom',
                    //     direction: 'row',
                    //     justify: false,
                    //     translateX: 0,
                    //     translateY: 56,
                    //     itemsSpacing: 0,
                    //     itemWidth: 100,
                    //     itemHeight: 18,
                    //     itemTextColor: '#999',
                    //     itemDirection: 'left-to-right',
                    //     itemOpacity: 1,
                    //     symbolSize: 18,
                    //     symbolShape: 'circle',
                    //     effects: [
                    //       {
                    //         on: 'hover',
                    //         style: {
                    //           itemTextColor: '#000'
                    //         }
                    //       }
                    //     ]
                    //   }
                    // ]}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </ResponsiveContext.Consumer>
      )
    )
  }
}
export const ApiLineGraph = withOpsApi(_LineGraph)

export class DashboardReportPage extends Component {
  /**
   * This loads donation data for month and year using child components and allows excel sheet download
   * */
  static contextType = AppContext
  defaultStore = { id: 'all', name: 'All Stores' }
  currentDate = new Date()
  colorMapping = buildNameToColorMapping()
  rangeOptions = [
    {
      label: '7 days',
      labelShort: '7D',
      month: undefined,
      day: 7,
      graphOptions: {
        bottomTickValues: 'every 1 days'
        // responsive: { bottomTickValues: 'every 10 days' }
      },
      filterParams: { annotate_entry_time: 'date' },
      zeroDays: true,
      adminOnly: true
    },
    {
      label: '2 weeks',
      labelShort: '2W',
      month: undefined,
      day: 14,
      graphOptions: {
        bottomTickValues: 'every 2 days'
        // responsive: { bottomTickValues: 'every 10 days' }
      },
      filterParams: { annotate_entry_time: 'date' },
      zeroDays: true,
      adminOnly: true
    },
    {
      label: '1 month',
      labelShort: '1M',
      month: undefined,
      graphOptions: {
        bottomTickValues: 'every 3 days'
        // responsive: { bottomTickValues: 'every 10 days' }
      },
      filterParams: { annotate_entry_time: 'date' },
      zeroDays: true
    },
    {
      label: '3 months',
      labelShort: '3M',
      month: 3,
      graphOptions: { bottomTickValues: 'every 1 weeks', rangeLabel: 'weekly' },
      filterParams: { annotate_entry_time: 'date-week' },
      zeroDays: false
    },
    {
      label: '6 months',
      labelShort: '6M',
      month: 6,
      graphOptions: { bottomTickValues: 'every 1 months', rangeLabel: 'weekly' },
      filterParams: { annotate_entry_time: 'date-week' },
      zeroDays: false
    },
    {
      label: '1 year',
      labelShort: '1Y',
      month: 12,
      graphOptions: {
        bottomTickValues: 'every 2 months',
        xScalePrecision: 'month',
        xFormat: '%y-%m',
        rangeLabel: 'monthly'
      },
      filterParams: { annotate_entry_time: 'date-month' },
      zeroDays: false
    },
    {
      label: '3 years',
      labelShort: '3Y',
      month: 36,
      graphOptions: {
        bottomTickValues: 'every 3 months',
        xScalePrecision: 'month',
        xFormat: '%y-%m',
        rangeLabel: 'monthly'
      },
      filterParams: { annotate_entry_time: 'date-month' },
      zeroDays: false
    },
    {
      label: 'Max',
      labelShort: 'MAX',
      month: 12 * 5,
      graphOptions: {
        bottomTickValues: 'every 4 months',
        xScalePrecision: 'month',
        xFormat: '%y-%m',
        rangeLabel: 'monthly'
      },
      filterParams: { annotate_entry_time: 'date-month' },
      zeroDays: false
    }
  ]
  state = {
    activeTabIndex: 0,
    yearStartDate: null,
    monthStartDate: null,
    monthEndDate: null,
    monthStartDateISO: '',
    monthEndDateISO: '',
    yearStartDateISO: '',
    heading: '',
    stores: [],
    searchedStores: undefined,
    rangeSetting: {},
    selectedStore: {},
    reportALoading: false,
    reportBLoading: false,
    reportCLoading: false,
    storesLoading: false,
    loadingChildren: false,
    impactMetrics: [],
    showRangeSettings: false
  }

  setReportLoading = (data) => {
    const { reportALoading, reportBLoading, storesLoading } = data
    if (reportALoading !== undefined) {
      this.setState({ reportALoading: reportALoading }, this.areChildrenLoading)
    }
    if (reportBLoading !== undefined) {
      this.setState({ reportBLoading: reportBLoading }, this.areChildrenLoading)
    }
    if (storesLoading !== undefined) {
      this.setState({ storesLoading: storesLoading }, this.areChildrenLoading)
    }
  }
  areChildrenLoading = () => {
    const { reportALoading, reportBLoading, storesLoading } = this.state
    let childStates = [reportALoading, reportBLoading, storesLoading]

    const loading = !childStates.every((s) => s === false)
    this.setState({ loadingChildren: loading })
  }
  onActiveTab = (nextIndex) => this.setState({ activeTabIndex: nextIndex })
  getColor = (data) => {
    return this.colorMapping.destinations[data.id] || this.colorMapping.departments[data.id]
  }
  componentDidMount() {
    const { path, url } = this.props.match
    firebase.analytics().logEvent('page_view', {
      page_location: url,
      page_path: path,
      page_title: 'Client Dashboard'
    })
    const date = new Date()
    date.setMonth(date.getMonth())
    const rangeSettings = this.rangeOptions[2] // month
    this.setState({ rangeSetting: rangeSettings })
    this.setLongDateRange(date, rangeSettings)
    this.context.stores ? this.loadStoresFromContext() : this.loadAllowedStores()
  }
  loadStoresFromContext() {
    const { stores = {} } = this.context
    const meshStores = [this.defaultStore].concat(Object.values(stores))

    this.setState(
      {
        stores: meshStores,
        selectedStore: this.defaultStore
      },
      () => this.setReportLoading({ storesLoading: false })
    )
  }
  loadAllowedStores = () => {
    let { companyUser = {} } = this.props
    let params = {}
    let url = 'reports/stores/'
    const defaultStore = this.defaultStore
    let allStores = [defaultStore]
    let companyUserStoreId =
      companyUser.report_store_id > -1 ? companyUser.report_store_id : companyUser.store

    if (companyUser.id) {
      if (companyUser.permissions.canAccessAllStores) {
        // company id required in params or authorization will fail
        params = { company_id: companyUser.company }
      } else {
        // if an account was not set up correctly and we must have a reporting store, prompt the user
        if (!companyUserStoreId) {
          this.context.actions.showResponseAsError({
            response: {
              data: {
                name: 'Account setup not completed',
                type: 'support_required',
                message:
                  // eslint-disable-next-line max-len
                  'Your account is missing a reporting store. Account setup has not been completed. Contact your FoodMesh representative to resolve this issue.'
              }
            }
          })
          return
        }
        // grab only one store at object level, this is the only authorized request
        url = url + (companyUserStoreId ? companyUserStoreId + '/' : '')
        allStores = []
      }
    }
    const response = opsAPI.getFromRetail(url, params)
    this.setReportLoading({ storesLoading: true })

    response
      .then((result) => {
        const stores = allStores.concat(result.data.results ? result.data.results : [result.data])
        this.setState(
          {
            stores: stores,
            selectedStore: companyUserStoreId
              ? stores.find((store) => store.id === companyUserStoreId) || {}
              : defaultStore
          },
          () => this.setReportLoading({ storesLoading: false })
        )
      })
      .catch((err) => {
        this.setReportLoading({ storesLoading: false })
        this.context.actions.showResponseAsError(err)
      })
  }
  getMonthYearAsString = () => {
    return (
      monthNames[this.state.monthEndDate.getMonth()] + ' ' + this.state.monthEndDate.getFullYear()
    )
  }
  getMonthRangeAsString = () => {
    // SERVER DATA IS ASSUMED TO BE IN PST, ISO FORMAT HAS BEEN adjusted to assume PST time range
    const mDateA = moment
      .tz(this.state.monthStartDateISO.split('T')[0], 'America/Vancouver')
      .format('ddd, MMMM Do YYYY')
    const mDateB = moment
      .tz(this.state.monthEndDateISO.split('T')[0], 'America/Vancouver')
      .format('ddd, MMMM Do YYYY')
    return mDateA + ' - ' + mDateB
  }
  getYearStartRangeAsString = () => {
    return (
      this.state.yearStartDate.toLocaleString() + ' - ' + this.state.monthEndDate.toLocaleString()
    )
  }
  setLongDateRange = (date = null, rangeSettings = {}) => {
    // const { month, day } = rangeSettings
    // const currentDate = date ? new Date(date) : new Date()
    // const offsetTimeZoneHours = currentDate.getTimezoneOffset() / 60
    // const currentYear = currentDate.getFullYear()
    // const currentMonth = currentDate.getMonth()
    // const currentDayDate = currentDate.getDate()
    // const monthStartDate = new Date(
    //   currentYear,
    //   month !== undefined ? currentMonth - month + 1 : currentMonth,
    //   day !== undefined ? currentDayDate - day + 1 : 1,
    //   -offsetTimeZoneHours,
    //   0,
    //   0
    // )
    // const monthEndDate = new Date(
    //   currentYear,
    //   currentMonth + 1,
    //   0,
    //   23 - offsetTimeZoneHours,
    //   59,
    //   59,
    //   59
    // )

    const { monthStartDate, monthEndDate, offsetTimeZoneHours } = setLongDateRange(
      date,
      rangeSettings
    )
    this.setState(
      {
        monthStartDateISO: monthStartDate.toISOString(),
        monthEndDateISO: monthEndDate.toISOString(),
        offsetTimeZoneHours: offsetTimeZoneHours
      },
      () => {
        monthStartDate.setHours(0)
        monthEndDate.setHours(0)
        this.setState({
          monthStartDate: new Date(monthStartDate),
          monthEndDate: new Date(monthEndDate)
        })
      }
    )
  }
  setRangeSetting = (rangeSettings, date = null) => {
    firebase.analytics().logEvent('dashboard', { set_range_settings: rangeSettings.label })

    this.setState({ rangeSetting: rangeSettings, showRangeSettings: false })
    this.setLongDateRange(date ? date : this.state.monthEndDate, rangeSettings)
  }
  updateMetrics = ({ groupedForPie }) => {
    let { companyUser = {} } = this.props
    this.setState({
      impactMetrics: calculateImpactMetrics(0, 0, {
        showMoneySaved: !companyUser.id
      })
    })
    // use the data grouped for the pie graph, it already has summary of total weights
    if (groupedForPie.destination_id) {
      // grab charity which is stored under id 1 based on destination id in retail db
      const kgCharity = groupedForPie.destination_id[1] && groupedForPie.destination_id[1].value
      // grab total for all destinations
      let kgTotal = 0
      Object.values(groupedForPie.destination_id).forEach((d) => {
        kgTotal += d.value
      })
      this.setState({
        impactMetrics: calculateImpactMetrics(kgCharity, kgTotal, {
          showMoneySaved: !companyUser.id
        })
      })
    }
  }
  updateMetricsByApi = (results) => {
    let { companyUser = {} } = this.props
    this.setState({
      impactMetrics: calculateImpactMetrics(0, 0, {
        showMoneySaved: !companyUser.id
      })
    })

    if (results) {
      const apiValues = getTotalByKeyFromResults(results, [
        'meals_provided__sum',
        'money_saved__sum',
        'food_rescued_kg__sum',
        'co2_emissions_kg__sum'
      ])
      const metricsObj = calculateImpactMetrics(
        0,
        0,
        {
          showMoneySaved: !companyUser.id
        },
        apiValues
      )
      this.setState({ impactMetrics: metricsObj })
    }
  }
  setSelectedStore = ({ option }) => {
    firebase.analytics().logEvent('dashboard', { set_selected_store: option.id })
    this.setState({ selectedStore: option, searchedStores: undefined })
  }
  searchStoreList = debounce((value) => {
    if (value && value.length > 1) {
      const exp = new RegExp(value.toLowerCase(), 'i')
      this.setState({
        searchedStores: this.state.stores.filter((o) => exp.test(o.name.toLowerCase())) || []
      })
    } else {
      this.setState({ searchedStores: undefined })
    }
  }, 500)
  flwReportButton = () => {
    const { companyUser = {} } = this.props
    if (
      companyUser.id &&
      companyUser.company === 1 &&
      companyUser.permissions &&
      companyUser.permissions.canAccessAllStores
    ) {
      // For SOF only + headoffice or admin
      return (
        <Button
          label={'FLW'}
          icon={<ShareRounded />}
          reverse
          primary
          color={'mesh-green-2'}
          onClick={() => {
            window.open(
              'https://analytics.zoho.com/open-view/2043516000006781293/6a6044d2e2e820678312887e6f68eed9',
              '_blank'
            )
          }}
        />
      )
    }
    return null
  }
  render() {
    const { apiParams = {}, companyUser = {}, ...rest } = this.props
    const {
      monthStartDate,
      monthEndDate,
      monthStartDateISO,
      monthEndDateISO,
      heading,
      activeTabIndex,
      loadingChildren,
      stores,
      searchedStores,
      selectedStore,
      rangeSetting,
      impactMetrics,
      impactMetricsTimestamp,
      showRangeSettings
    } = this.state
    const passDownApiParams = {
      ...apiParams,
      annotate_entry_time: ['date'],
      entry_time__gte: monthStartDateISO,
      entry_time__lte: monthEndDateISO,
      ...(companyUser.id && { from_company_id: companyUser.company }),
      ...(selectedStore.id && selectedStore.id !== 'all' && { from_store_id: selectedStore.id }),
      ...(rangeSetting.label && rangeSetting.filterParams)
    }
    const rangeOptions = companyUser.id
      ? this.rangeOptions.filter((o) => o.adminOnly === undefined)
      : this.rangeOptions
    return (
      monthStartDate &&
      stores.length > 0 && (
        <AnimatePresence inital={false}>
          <Box fill background={'light-2'}>
            <Main
              flex={'shrink'}
            // pad={{ horizontal: 'none', top: 'bo', bottom: 'large' }}
            >
              <LoadingHeader background="light-2" loading={loadingChildren} />

              <PageHeader pad="medium" direction={'row-responsive'} heading={selectedStore.name}>
                <Text weight="bold" color="mesh-accent-dark">
                  {this.getMonthRangeAsString()}
                </Text>

                <Box flex={'grow'} />
                {this.flwReportButton()}
                <Select
                  size="small"
                  labelKey="name"
                  valueKey={{ key: 'id', reduce: true }}
                  options={searchedStores || stores}
                  onChange={this.setSelectedStore}
                  value={selectedStore.id ? selectedStore.id : this.defaultStore.id}
                  onSearch={this.searchStoreList}
                />
                <DropWithHeading
                  heading={'Reporting Range'}
                  label={rangeSetting.label || 'range'}
                  color={'light-2'}
                  reverse
                  hoverIndicator={false}
                  // primary
                  fill={'vertical'}
                  icon={<FormDown />}
                  open={showRangeSettings}
                  disabled={loadingChildren}
                  onOpen={() => this.setState({ showRangeSettings: true })}
                  onClose={() => this.setState({ showRangeSettings: false })}
                >
                  {rangeOptions.map((range) => (
                    <Button
                      key={range.label}
                      color={'light-1'}
                      size={'small'}
                      disabled={rangeSetting.label === range.label}
                      primary
                      active={rangeSetting.label === range.label}
                      label={range.label}
                      onClick={() => this.setRangeSetting(range)}
                    />
                  ))}
                </DropWithHeading>
                <SelectMonthInYear
                  disabled={loadingChildren}
                  endDate={this.currentDate}
                  offsetEndMonth={0}
                  selectAction={({ year, month }) =>
                    this.setRangeSetting(this.rangeOptions[2], new Date(year, month, 15))
                  }
                />
              </PageHeader>

              <Box
                align="start"
                fill="horizontal"
                pad={{ horizontal: 'large', top: 'medium' }}
                gap={'large'}
              >
                <DonationMetrics
                  flex={false}
                  loading={loadingChildren}
                  metrics={impactMetrics}
                  // timestamp={impactMetricsTimestamp}
                  direction={'row-responsive'}
                  metricWidth={'100%'}
                />

                <ApiLineGraph
                  show={true}
                  legend={this.colorMapping.departments}
                  key={'monthly-line-2'}
                  apiPath={'retail/reports/donation_totals'}
                  graphOptions={{
                    colors: this.getColor,
                    ...rangeSetting.graphOptions,
                    groupOptions: {
                      keys: ['from_department_id'],
                      barConfig: {
                        keyPair: {
                          idKey: 'date',
                          groupByFn: getDepartmentForId,
                          groupByKeyValue: 'from_department_id',
                          valueKey: 'weight_kg__sum',
                          keys: [...departments].sort(sortByColourOrder).map((item) => item.name),
                          xFill: {
                            min: monthStartDateISO && monthStartDateISO.split('T')[0],
                            max: monthEndDateISO && monthEndDateISO.split('T')[0],
                            addZeroDays: rangeSetting.zeroDays
                          }
                        }
                      },
                      lineConfig: {
                        keyPair: {
                          x: 'date',
                          y: 'weight_kg__sum',
                          xFill: {
                            min: monthStartDateISO && monthStartDateISO.split('T')[0],
                            max: monthEndDateISO && monthEndDateISO.split('T')[0],
                            addZeroDays: rangeSetting.zeroDays
                          }
                        },
                        idFn: getDepartmentForId
                      }
                    }
                  }}
                  apiParams={{
                    ...passDownApiParams,
                    ...{
                      annotate: ['from_department_id'],
                      annotate_metrics: true
                    }
                  }}
                  heading={'By department'}
                  {...rest}
                  loaderProps={{ loader: 'propagate' }}
                  // updateTotals={this.updateMonthly}
                  loadingStateAction={(value) => this.setReportLoading({ reportALoading: value })}
                  onResultsChanged={this.updateMetricsByApi}
                />
                <ApiLineGraph
                  show={true}
                  key={'monthly-line'}
                  legend={this.colorMapping.destinations}
                  apiPath={'retail/reports/donation_totals'}
                  graphOptions={{
                    colors: this.getColor,
                    ...rangeSetting.graphOptions,
                    // colors: { scheme: 'set2' },
                    groupOptions: {
                      barConfig: {
                        keyPair: {
                          idKey: 'date',
                          groupByFn: getDestinationForId,
                          groupByKeyValue: 'destination_id',
                          valueKey: 'weight_kg__sum',
                          keys: [...destinations].sort(sortByColourOrder).map((item) => item.name),
                          xFill: {
                            min: monthStartDateISO && monthStartDateISO.split('T')[0],
                            max: monthEndDateISO && monthEndDateISO.split('T')[0],
                            addZeroDays: rangeSetting.zeroDays
                          }
                        }
                      },
                      keys: ['destination_id'],
                      lineConfig: {
                        keyPair: {
                          x: 'date',
                          y: 'weight_kg__sum',
                          xFill: {
                            min: monthStartDateISO && monthStartDateISO.split('T')[0],
                            max: monthEndDateISO && monthEndDateISO.split('T')[0],
                            addZeroDays: rangeSetting.zeroDays
                          }
                        },
                        idFn: getDestinationForId
                      }
                    }
                  }}
                  apiParams={{
                    ...passDownApiParams,
                    ...{
                      annotate: ['destination_id']
                    }
                  }}
                  heading={'By destination'}
                  {...rest}
                  loaderProps={{ loader: 'propagate' }}
                  // onGraphDataChanged={this.updateMetrics}
                  loadingStateAction={(value) => this.setReportLoading({ reportBLoading: value })}
                />

                <Box pad={'large'} />
              </Box>
            </Main>
          </Box>
        </AnimatePresence>
      )
    )
  }
}
